// Lab-test selection for a soil-sampling ORDER: when ordering a soil test the user picks which lab
// panel to run, and can ADD a Haney soil-health test — either on every sample or on one out of
// every N samples (at least 1 per field).
import {
  CORES,
  CORE_LINE_FT,
  DEPTH_IN,
  clampCoreLineFt,
  clampCores,
  clampDepthIn,
  formatUsPhone,
  normalizeUsPhone,
} from "./orderOptions";
import { DEFAULT_LAB_KEY, defaultPackageFor, labByKey } from "./soilLabs";

export interface HaneyFrequency {
  enabled: boolean; // include the Haney soil-health test in this order
  everySample: boolean; // true = Haney on EVERY sample; false = one out of `oneOutOf`
  oneOutOf: number; // when !everySample: run Haney on 1 out of every N samples (min 1 per field)
}
export interface LabOrderConfig {
  panel: string;
  haney: HaneyFrequency;
  /* * sampling depth, inches (default 8, 1–12) */
  depthIn: number;
  /* * the lab (lib/soilLabs.ts key) */
  lab: string;
  /* * the lab's package code (lib/soilLabs.ts) */
  labPackage: string;
  /* * cores per sample (default 6, 4–8) */
  coresPerSample: number;
  /* * core line length, feet (default 20, 5–200) */
  coreLineFt: number;
  /* * sampling-notification phone (US); '' = none. Normalized to E.164 on the wire */
  notifyPhone: string;
}

export const LAB_PANELS = [
  { key: "standard", label: "Standard soil test (pH, P, K, OM, CEC)" },
  { key: "complete", label: "Complete (standard + Ca, Mg, micronutrients)" },
] as const;

export const DEFAULT_LAB_ORDER: LabOrderConfig = {
  panel: "standard",
  haney: { enabled: false, everySample: true, oneOutOf: 5 },
  depthIn: DEPTH_IN.default,
  lab: DEFAULT_LAB_KEY,
  labPackage: defaultPackageFor(DEFAULT_LAB_KEY),
  coresPerSample: CORES.default,
  coreLineFt: CORE_LINE_FT.default,
  notifyPhone: "",
};

// Normalize the frequency divisor to a valid integer ≥ 1 (the UI allows free typing).
export const haneyDivisor = (h: HaneyFrequency): number =>
  Math.max(1, Math.floor(h.oneOutOf) || 1);

// Which of the `count` samples (in numbered order) get the Haney test. Every Nth sample, INDEX 0
// always included — that guarantees the "min 1 per field" rule even when N exceeds the sample count.
export function haneyPointFlags(count: number, h: HaneyFrequency): boolean[] {
  if (!h.enabled || count <= 0)
    return new Array(Math.max(0, count)).fill(false);
  if (h.everySample) return new Array(count).fill(true);
  const n = haneyDivisor(h);
  return Array.from({ length: count }, (_, i) => i % n === 0);
}

// How many samples will get the Haney test (for the order summary / work-order display).
export const haneyCount = (count: number, h: HaneyFrequency): number =>
  haneyPointFlags(count, h).filter(Boolean).length;

// A short human summary of the lab selection (order confirmations, Work orders page). MUST be total —
// it runs inside the Work-orders queryFn over server-echoed config, so a malformed/legacy `lab` (present
// but missing a well-formed `haney`, or a bare string) must degrade gracefully, never throw (else it
// would sink the whole page — the codebase's "never crash the read" contract).
export function labSummary(cfg: LabOrderConfig): string {
  const lab = cfg?.lab ? labByKey(cfg.lab) : undefined;
  const pkg =
    lab && cfg?.labPackage
      ? lab.packages.find((p) => p.code === cfg.labPackage)
      : undefined;
  const head = lab
    ? `${lab.shortName}${pkg ? ` · ${pkg.code}` : cfg?.labPackage ? ` · ${cfg.labPackage}` : ""}`
    : (LAB_PANELS.find((p) => p.key === cfg?.panel)?.label ??
      cfg?.panel ??
      "Soil test");
  const parts: string[] = [head];
  const h = cfg?.haney;
  if (h?.enabled)
    parts.push(
      `Haney (${h.everySample ? "every sample" : `1 of every ${haneyDivisor(h)} samples`})`,
    );
  if (Number.isFinite(cfg?.depthIn)) parts.push(`${cfg.depthIn}″ depth`);
  if (
    Number.isFinite(cfg?.coresPerSample) ||
    Number.isFinite(cfg?.coreLineFt)
  ) {
    parts.push(
      `${Number.isFinite(cfg?.coresPerSample) ? `${cfg.coresPerSample} cores` : ""}${Number.isFinite(cfg?.coresPerSample) && Number.isFinite(cfg?.coreLineFt) ? " × " : ""}${Number.isFinite(cfg?.coreLineFt) ? `${cfg.coreLineFt} ft line` : ""}`,
    );
  }
  if (cfg?.notifyPhone) parts.push(`SMS ${formatUsPhone(cfg.notifyPhone)}`);
  return parts.join(" · ");
}

// The lab block for the SS2 order `config.lab` — clamps the divisor for the wire.
export function labForWire(cfg: LabOrderConfig): LabOrderConfig {
  return {
    panel: cfg.panel,
    haney: {
      enabled: cfg.haney.enabled,
      everySample: cfg.haney.everySample,
      oneOutOf: haneyDivisor(cfg.haney),
    },
    // the order options, clamped to their ranges; the phone in E.164 (or '' when none)
    depthIn: clampDepthIn(cfg.depthIn),
    lab: cfg.lab,
    labPackage: cfg.labPackage,
    coresPerSample: clampCores(cfg.coresPerSample),
    coreLineFt: clampCoreLineFt(cfg.coreLineFt),
    notifyPhone: normalizeUsPhone(cfg.notifyPhone) ?? "",
  };
}
